import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { addDays } from 'date-fns';
import { Box, Button, createStyles, makeStyles } from '@material-ui/core';

import {
  BookingRequest,
  bookingService,
  FormStatus,
  guestProfileQuery,
  guestProfileService,
  propertyQuery,
  ReservationForms,
  reservationService,
  RoomRateForms,
  roomRateQuery,
  roomRateService,
  RoomType,
  roomTypeQuery,
  sessionQuery,
  corporateAccountQuery,
  corporateAccountService,
  ContactType,
} from '@lib/state';
import {
  FormHub,
  getAcceptCardData,
  GuestForm,
  LocationForm,
  LocationFormFields,
  PaymentDetailsForm,
  ReservationConfirmation,
  ResponsiveContainer,
  RoomRateForm,
  useBookingStatus,
  useCurrentUserId,
  useFormEvents,
  useObservable,
  useRoomRates,
  withPropertyFeatures,
} from '@lib/common';
import { SignInModal } from '../../account/components';
import { LandingContent } from '../components';

const useStyles = makeStyles(() =>
  createStyles({
    hidden: {
      display: 'none',
    },
  })
);

const CreateReservation: React.FC<RouteComponentProps> = ({ location }) => {
  const styles = useStyles();

  const [corporateAccountSelected, setCorporateAccountSelected] = useState<string | undefined>(
    undefined
  );
  const [roomType, setRoomType] = useState<RoomType | undefined>(undefined);
  const [editLocation, setEditLocation] = useState(true);
  const [locationFormError, setLocationFormError] = useState<Error | undefined>(undefined);
  const [requiresPayment, setRequiresPayment] = useState(true);

  const [didPerformSearch, setDidPerformSearch] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const [{ error, status }, resetReservationFormStatus] = useFormEvents(
    ReservationForms.CreateReservation
  );
  const [{ error: availabilityError, status: availabilityStatus }] = useFormEvents(
    RoomRateForms.Availability
  );

  const properties = useObservable(propertyQuery.openProperties);
  const roomTypes = useObservable(roomTypeQuery.propertyRoomTypes);

  const isLoggedIn = useObservable(sessionQuery.isLoggedIn);
  const guest = useObservable(guestProfileQuery.guest);
  const myCorporateAccounts = useObservable(corporateAccountQuery.accounts);

  const userId = useCurrentUserId();
  const bookingStatus = useBookingStatus();

  const promoCode = useMemo(() => new URLSearchParams(location.search).get('promo'), [location]);

  const defaultRoomType = useMemo(() => {
    const lowestValue = Math.min(...roomTypes.map(r => r.sortOrder ?? 0));
    return roomTypes.find(r => r.sortOrder === lowestValue);
  }, [roomTypes]);

  const {
    uiState,
    roomRates,
    checkInStartOfDay,
    checkOutStartOfDay,
    checkInDefaultDate,
    corporateBooking,
    policy,
    quantity,
    guestsPerRoom,
    roomTypeId,
    costCenterOptions,
    loading,
    selectedRatePlan,
  } = useRoomRates(userId, corporateAccountSelected);

  useEffect(() => {
    const roomType = roomTypes.find(r => r.id === roomTypeId);
    setRoomType(roomType);
  }, [roomTypeId, roomTypes]);

  useEffect(() => {
    // do nothing if the corporate account is already selected
    if (corporateAccountSelected) return;

    setCorporateAccountSelected(corporateBooking?.id ?? myCorporateAccounts[0]?.id);
  }, [corporateBooking, myCorporateAccounts, corporateAccountSelected]);

  useEffect(() => {
    //reset the reservation form status when the quantity is changed
    resetReservationFormStatus();
  }, [quantity, resetReservationFormStatus]);

  useEffect(() => {
    if (isLoggedIn) {
      corporateAccountService.getMyAccounts();
      guestProfileService.getGuestProfile();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (availabilityStatus === FormStatus.Success && didPerformSearch) {
      setEditLocation(false);
    }
  }, [availabilityStatus, didPerformSearch]);

  const backToLanding = useCallback(() => {
    setEditLocation(true);
    setDidPerformSearch(false);
    roomRateService.resetUI();
  }, []);

  useEffect(() => {
    // header section links and Book Now target the landing; from the rate/guest/payment steps go back to it, as "edit location" does
    if (!editLocation && (location.hash || new URLSearchParams(location.search).has('booking')))
      backToLanding();
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      reservationService.selectReservation();
      roomRateService.selectRoomRate();
      roomRateService.updateUI({ roomTypeId: undefined });
    };
  }, []);

  const submitLocationForm = useCallback(
    (values: LocationFormFields) => {
      setCorporateAccountSelected(values.corporateAccountId ?? undefined);
      const newRoomType = roomTypes.find(r => r.id === values.roomTypeId);
      roomRateService.updateUI({
        ...values,
        promoCode,
        guestsPerRoom: newRoomType?.totalOccupancy,
      });
      setDidPerformSearch(true);
      resetReservationFormStatus();
      setLocationFormError(undefined);
    },
    [resetReservationFormStatus, promoCode, roomTypes, setLocationFormError]
  );

  const submit = useCallback(
    ({ password, card, ...booking }: any) => {
      const reservationQuantity = !booking.guestTbd
        ? booking.quantity * guestsPerRoom
        : booking.quantity * guestsPerRoom - 1;

      const request: BookingRequest = {
        card: getAcceptCardData(card),
        ...booking,
        quantity: reservationQuantity,
        corporateAccountId: corporateAccountSelected,
        rooms: booking.guestTbd
          ? booking.rooms.filter(
              (r: { group: string }) => r.group !== reservationQuantity.toString()
            )
          : booking.rooms,
        fields: booking.fields?.filter((f: { key: string }) => f.key) ?? [],
      };

      if (userId) request.contact.userId = userId;
      if (uiState.promoCode) request.promoCode = uiState.promoCode;

      if (request.contact.contactType === ContactType.Guest)
        request.contact.vipStatus = guest?.vipStatus;

      if (isLoggedIn) {
        bookingService.createReservation(
          request,
          !!corporateAccountSelected || request.contact.contactType === ContactType.Delegate
        );
      } else {
        bookingService.createAccountAndReservation(password, request);
      }
    },
    [uiState, isLoggedIn, userId, corporateAccountSelected, guest, guestsPerRoom]
  );

  const onRateFormLoadFail = useCallback(() => {
    //Prevent from firing early
    if (!editLocation) {
      setLocationFormError(new Error('There was an error. Please try again.'));
      setEditLocation(true);
      setDidPerformSearch(false);
      roomRateService.resetUI();
    }
  }, [setEditLocation, setDidPerformSearch, setLocationFormError, editLocation]);

  const onBookAgainClick = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <Box maxWidth="100%" pb={editLocation ? 0 : 3}>
      {showLoginModal && (
        <SignInModal
          show={true}
          redirectPath={location.pathname}
          onClose={() => setShowLoginModal(false)}
        />
      )}
      {bookingStatus ? (
        <ResponsiveContainer disableGutters>
          <ReservationConfirmation
            reservations={bookingStatus.reservations ?? []}
            failures={bookingStatus.failures ?? []}
            consumedHolds={bookingStatus.consumedHolds}
            properties={properties}
            policy={policy}
            variant="contained"
            onBookAgain={onBookAgainClick}
          />
        </ResponsiveContainer>
      ) : (
        <FormHub onSubmit={submit}>
          {editLocation && (
            <LandingContent>
              <LocationForm
                //Component props
                itemSpacing={2}
                locationLabel="Location"
                locationPlaceholder="Select a location"
                roomTypePlaceholder="—"
                //Reservation details props
                properties={properties}
                corporateAccounts={myCorporateAccounts}
                minCheckIn={checkInDefaultDate}
                roomTypes={roomTypes}
                defaultPropertyId={uiState.propertyId}
                defaultCorporateAccountId={corporateAccountSelected}
                defaultCheckIn={checkInStartOfDay}
                defaultCheckOut={
                  checkOutStartOfDay ?? (checkInStartOfDay && addDays(checkInStartOfDay, 1))
                }
                defaultRoomTypeId={defaultRoomType?.id}
                defaultQuantity={quantity}
                //Form props
                onStepComplete={submitLocationForm}
                status={availabilityStatus}
                error={availabilityError ?? locationFormError}
              />
            </LandingContent>
          )}
          <ResponsiveContainer className={editLocation ? styles.hidden : undefined} disableGutters>
            {!editLocation && quantity && roomRates?.length > 0 && (
              <>
                <RoomRateForm
                  //Component props
                  variant="contained"
                  //Reservation details props
                  properties={properties}
                  quantity={quantity * guestsPerRoom}
                  promo={promoCode}
                  policy={policy}
                  selectedRate={selectedRatePlan}
                  roomType={roomType}
                  //Form props
                  onEditLocation={backToLanding}
                  onLoadFail={onRateFormLoadFail}
                  setRequiresPayment={setRequiresPayment}
                  status={availabilityStatus}
                  availabilityError={availabilityError ?? locationFormError}
                  multipleCorpAccts={myCorporateAccounts.length > 1}
                  isLoading={loading || availabilityStatus === FormStatus.Pending}
                  isSingle={guestsPerRoom === 1}
                />
                <GuestForm
                  guest={guest}
                  variant="contained"
                  corporateBooking={corporateBooking}
                  costCenterOptions={costCenterOptions}
                  quantity={quantity}
                  guestCount={roomType?.adultOccupancy?.maxOccupancy ?? undefined}
                  useRearCam={false}
                  resetReservationFormStatus={resetReservationFormStatus}
                  guestsPerRoom={guestsPerRoom}
                >
                  {!isLoggedIn && !editLocation && (
                    <Box display="flex" justifyContent="center" mt={1} mb={1}>
                      <Button
                        onClick={() => setShowLoginModal(true)}
                        color="secondary"
                        variant="contained"
                        data-testid="guestFormLoginButton"
                      >
                        Sign in to existing account
                      </Button>
                    </Box>
                  )}
                </GuestForm>
              </>
            )}
            <PaymentDetailsForm
              status={status}
              error={error ?? availabilityError}
              requiresPayment={requiresPayment}
              variant="contained"
              corporateAccountId={corporateAccountSelected}
            />
          </ResponsiveContainer>
        </FormHub>
      )}
    </Box>
  );
};

export const CreateReservationPage = withPropertyFeatures(
  CreateReservation,
  roomRateQuery.propertyId
);
